/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { EnvDriftBoard } from './types';

export type EnvDriftAccess = {
  allowed: boolean | undefined; // undefined while loading
};

// Module-level cache: sidebar probe and page share one round-trip (same
// convention as useNetworkPolicyAccess).
let cached: EnvDriftAccess | undefined;
let inflight: Promise<EnvDriftAccess> | undefined;

export const useEnvDriftAccess = (): EnvDriftAccess => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<EnvDriftAccess>(
    cached ?? { allowed: undefined },
  );

  useEffect(() => {
    if (cached !== undefined) {
      setState(cached);
      return undefined;
    }
    let active = true;
    inflight =
      inflight ??
      (async () => {
        try {
          const base = await discoveryApi.getBaseUrl('env-drift');
          const res = await fetchApi.fetch(`${base}/access`);
          if (!res.ok) return { allowed: false };
          const json = await res.json().catch(() => ({}));
          return { allowed: json.allowed === true };
        } catch {
          return { allowed: false };
        }
      })();
    inflight.then(result => {
      if (result.allowed) cached = result;
      else inflight = undefined; // transient miss: re-probe on next mount
      if (active) setState(result);
    });
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi]);

  return state;
};

export const useEnvDriftApi = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      const base = await discoveryApi.getBaseUrl('env-drift');
      const res = await fetchApi.fetch(`${base}${path}`, init);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      return body;
    },
    [discoveryApi, fetchApi],
  );

  const pairQuery = (from?: string, to?: string) =>
    from && to
      ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
      : '';

  const getBoard = useCallback(
    (from?: string, to?: string): Promise<EnvDriftBoard> =>
      call(`/board${pairQuery(from, to)}`),
    [call],
  );

  const refreshBoard = useCallback(
    (from?: string, to?: string): Promise<EnvDriftBoard> =>
      call(`/refresh${pairQuery(from, to)}`, { method: 'POST' }),
    [call],
  );

  // Stable identity: consumers hang effects off this object (same note as
  // useNetworkPolicyApi).
  return useMemo(() => ({ getBoard, refreshBoard }), [getBoard, refreshBoard]);
};
